/**
 * AgencyBrandingTab.jsx
 * ------------------------------------------------------------------
 * Agency-level branding settings: logo, brand color, company name,
 * contact info, footer text. Saved values are injected into the
 * white-label PDF report exposed via `/agency/clients/{id}/report.pdf`.
 *
 * Logo is captured as a base64 data URL (no separate /upload route
 * needed) — capped server-side at 200 KB so PDFs stay small.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config";
import {
  Save, Loader2, Upload, Image as ImageIcon, AlertTriangle, Check,
  Palette, X,
} from "lucide-react";

const FIELDS = [
  { key: "company_name",  label: "Názov agentúry",          type: "text" },
  { key: "contact_email", label: "Kontaktný email",          type: "email" },
  { key: "contact_phone", label: "Telefón",                 type: "text" },
  { key: "website",       label: "Web",                      type: "url" },
  { key: "footer_text",   label: "Footer text v PDF",        type: "text" },
];

export default function AgencyBrandingTab({ userEmail }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userEmail) return;
    axios.get(`${API}/agency/branding`, {
      headers: { "X-User-Email": userEmail },
    }).then(r => setForm(r.data || {}))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userEmail]);

  const handleField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleLogoFile = (file) => {
    if (!file) return;
    if (file.size > 250 * 1024) {
      setError("Logo musí byť max 200 KB. Použi optimalizovaný PNG / SVG.");
      return;
    }
    setError(null);
    const reader = new FileReader();
    reader.onload = () => handleField("logo_data_url", reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true); setError(null); setSaved(false);
    try {
      await axios.put(`${API}/agency/branding`, form, {
        headers: { "X-User-Email": userEmail },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> Načítam branding…</div>;
  }

  const logoUrl = form.logo_data_url || "";

  return (
    <div className="space-y-6" data-testid="agency-branding-tab">
      <header>
        <div className="flex items-center gap-2 mb-1">
          <Palette className="w-4 h-4 text-purple-400" />
          <div className="text-xs uppercase tracking-widest text-purple-400">White-label branding</div>
        </div>
        <h2 className="text-lg font-bold">Vlastné branding pre PDF reporty</h2>
        <p className="text-xs text-gray-400 mt-1">
          Logo + farba + kontakt sa použijú v každom PDF reporte ktorý dáš klientovi.
        </p>
      </header>

      {/* Logo uploader */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm">
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-purple-400" /> Logo
        </h3>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-40 h-20 bg-white border border-gray-800 rounded-sm flex items-center justify-center overflow-hidden">
            {logoUrl ? (
              <img src={logoUrl} alt="Agency logo" className="max-w-full max-h-full object-contain" data-testid="agency-branding-logo-preview" />
            ) : (
              <span className="text-xs text-gray-400">Žiadne logo</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white text-sm rounded-sm cursor-pointer inline-flex items-center gap-1">
              <Upload className="w-3.5 h-3.5" /> Nahrať logo
              <input
                type="file" accept="image/*" className="hidden"
                onChange={(e) => handleLogoFile(e.target.files?.[0])}
                data-testid="agency-branding-logo-input"
              />
            </label>
            {logoUrl && (
              <button
                onClick={() => handleField("logo_data_url", "")}
                data-testid="agency-branding-logo-remove"
                className="px-2 py-2 bg-gray-800 hover:bg-rose-900 text-gray-300 hover:text-rose-200 text-sm rounded-sm inline-flex items-center"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </div>
        <p className="text-[10px] text-gray-500 mt-2">Max 200 KB. Odporúčaný formát PNG / SVG, šírka 400px.</p>
      </section>

      {/* Brand color */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm">
        <h3 className="text-sm font-bold mb-3">Farba značky</h3>
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={form.brand_color || "#7c3aed"}
            onChange={(e) => handleField("brand_color", e.target.value)}
            data-testid="agency-branding-color-picker"
            className="w-16 h-10 bg-transparent border border-gray-800 rounded-sm cursor-pointer"
          />
          <input
            type="text"
            value={form.brand_color || "#7c3aed"}
            onChange={(e) => handleField("brand_color", e.target.value)}
            data-testid="agency-branding-color-hex"
            className="w-32 px-2 py-1.5 bg-black/50 border border-gray-800 focus:border-purple-500 text-gray-200 font-mono text-sm rounded-sm focus:outline-none"
          />
          <span
            className="px-3 py-1.5 text-sm font-bold rounded-sm text-white"
            style={{ backgroundColor: form.brand_color || "#7c3aed" }}
            data-testid="agency-branding-color-preview"
          >
            Preview chip
          </span>
        </div>
      </section>

      {/* Text fields */}
      <section className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm grid grid-cols-1 md:grid-cols-2 gap-3">
        {FIELDS.map(f => (
          <label key={f.key} className="text-xs">
            <span className="text-gray-400 block mb-1">{f.label}</span>
            <input
              type={f.type}
              value={form[f.key] || ""}
              onChange={(e) => handleField(f.key, e.target.value)}
              data-testid={`agency-branding-${f.key}`}
              className="w-full px-2 py-1.5 bg-black/50 border border-gray-800 focus:border-purple-500 text-white rounded-sm focus:outline-none"
            />
          </label>
        ))}
        <label className="flex items-center gap-2 text-xs text-gray-300 col-span-full cursor-pointer pt-2">
          <input
            type="checkbox"
            checked={form.show_powered_by !== false}
            onChange={(e) => handleField("show_powered_by", e.target.checked)}
            data-testid="agency-branding-powered-by"
            className="accent-purple-500"
          />
          Zobraziť "Powered by TopRankPulse" v päte PDF
        </label>
      </section>

      {error && (
        <div className="p-3 bg-rose-900/20 border border-rose-700/40 rounded-sm text-sm text-rose-200 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5" /> {String(error)}
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="agency-branding-save"
          className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white font-bold text-sm rounded-sm inline-flex items-center gap-1"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Uložiť
        </button>
        {saved && (
          <span className="text-emerald-300 text-xs inline-flex items-center gap-1" data-testid="agency-branding-saved">
            <Check className="w-3.5 h-3.5" /> Uložené
          </span>
        )}
      </div>
    </div>
  );
}
